import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  DocumentTextIcon,
  ArrowLeftIcon,
  PencilIcon,
  TrashIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  CurrencyDollarIcon,
  CalendarIcon,
  UserGroupIcon,
  BuildingIcon,
  PrinterIcon,
  ArrowDownTrayIcon,
  PlusIcon,
  XMarkIcon,
  EyeIcon
} from '@heroicons/react/24/outline';

const EUR_RATE = 1.95583; // Euro to BGN conversion rate

const IncomingInvoiceDetails = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [invoice, setInvoice] = useState(null);
  const [partner, setPartner] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // Define extractItemsFromOCR function before hooks
  const extractItemsFromOCR = (ocrText) => {
    if (!ocrText) return [];
    
    const lines = ocrText.split('\n');
    const items = [];
    
    console.log('🔍 Starting Bulgarian invoice items extraction...');
    console.log('📝 OCR text length:', ocrText.length);
    
    // Find the items section
    let itemsStartIndex = -1;
    let itemsEndIndex = -1;
    
    // Look for the items section header
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();
      if (line.includes('Наименование на стоката') || line.includes('Наименование')) {
        itemsStartIndex = i;
        console.log('🔍 Found items section start at line', i, ':', line);
        break;
      }
    }
    
    // Find the end of items section
    for (let i = itemsStartIndex + 1; i < lines.length; i++) {
      const line = lines[i].trim();
      if (line.includes('Словом') || line.includes('Дата на данъчно')) {
        itemsEndIndex = i;
        console.log('🔍 Found items section end at line', i, ':', line);
        break;
      }
    }
    
    if (itemsEndIndex === -1) {
      itemsEndIndex = lines.length;
    }
    
    // Extract items using column-based approach
    const itemLines = [];
    
    // Collect all lines in the items section
    for (let i = itemsStartIndex; i < itemsEndIndex; i++) {
      const line = lines[i].trim();
      if (line.length > 0 && !line.includes('Наименование') && !line.includes('Мярка') && 
          !line.includes('Количество') && !line.includes('Цена') && !line.includes('Сума')) {
        itemLines.push(line);
      }
    }
    
    console.log('🔍 Item lines found:', itemLines);
    
    // Try to extract items using a more sophisticated column-based approach
    const extractedItems = [];
    
    // First, try to find the exact pattern from the Bulgarian invoice
    const allText = itemLines.join(' ');
    console.log('🔍 Combined item text:', allText);
    
    // Look for specific item patterns in the combined text
    const itemPatterns = [
      // TeMAX format: "1 48224 ВИНТОВЕ САМОПРОБИВНИ 4.8X35/КУТ.250БР 1 бр 20.99 20.99 0 20.99"
      /(\d+)\s+(\d+)\s+([А-Яа-я\s\/\.]+)\s+(\d+)\s+(бр\.?|M|шт|кг|л|м|м2|м3)\s+(\d+\.?\d*)\s+(\d+\.?\d*)\s+(\d+\.?\d*)\s+(\d+\.?\d*)\s+(\d+\.?\d*)/g,
      // Generic pattern for any item with number, name, unit, quantity, price, total
      /(\d+)\s+([А-Яа-я\s]+)\s+(бр\.?|M|шт|кг|л|м|м2|м3)\s+(\d+\.?\d*)\s+(\d+\.?\d*)\s+(\d+\.?\d*)/g,
      // Pattern for "1 Букса бр 1.00 20.00 20.00"
      /(\d+)\s+(Букса|Отвертка|Кабел)\s+(бр\.?|M)\s+(\d+\.?\d*)\s+(\d+\.?\d*)\s+(\d+\.?\d*)/g,
      // Pattern for "2 Букса бр. 1.00 15.00 15.00"
      /(\d+)\s+(Букса|Отвертка|Кабел)\s+(бр\.?|M)\s+(\d+\.?\d*)\s+(\d+\.?\d*)\s+(\d+\.?\d*)/g,
      // Pattern for "3 Отвертка бр 1.00 2.08 2.08"
      /(\d+)\s+(Букса|Отвертка|Кабел)\s+(бр\.?|M)\s+(\d+\.?\d*)\s+(\d+\.?\d*)\s+(\d+\.?\d*)/g,
      // Pattern for "4 Кабел M 15.00 3.75 56.25"
      /(\d+)\s+(Букса|Отвертка|Кабел)\s+(бр\.?|M)\s+(\d+\.?\d*)\s+(\d+\.?\d*)\s+(\d+\.?\d*)/g
    ];
    
    for (let i = 0; i < itemPatterns.length; i++) {
      const pattern = itemPatterns[i];
      let match;
      while ((match = pattern.exec(allText)) !== null) {
        let item;
        
        if (i === 0) {
          // TeMAX format: match[1]=row, match[2]=artCode, match[3]=name, match[4]=qty, match[5]=unit, match[6]=price1, match[7]=price2, match[8]=vat, match[9]=total1, match[10]=total2
          item = {
            number: match[1],
            name: match[3].trim(),
            unit: match[5],
            quantity: parseFloat(match[4]),
            price: parseFloat(match[6]),
            total: parseFloat(match[9]) || parseFloat(match[10]) || parseFloat(match[7])
          };
        } else {
          // Standard format: match[1]=number, match[2]=name, match[3]=unit, match[4]=quantity, match[5]=price, match[6]=total
          item = {
            number: match[1],
            name: match[2],
            unit: match[3],
            quantity: parseFloat(match[4]),
            price: parseFloat(match[5]),
            total: parseFloat(match[6])
          };
        }
        
        if (!isNaN(item.quantity) && !isNaN(item.price) && !isNaN(item.total)) {
          console.log('✅ Extracted item with pattern', i, ':', item);
          extractedItems.push(item);
        }
      }
    }
    
    // If we found items with patterns, return them
    if (extractedItems.length > 0) {
      console.log('🔍 Extracted', extractedItems.length, 'items with patterns');
      return extractedItems;
    }
    
    // If no patterns found, try to extract from the raw OCR text more carefully
    console.log('🔍 No items found with patterns, trying raw OCR extraction...');
    
    // Try to find items by looking for lines with numbers and prices
    const rawItems = [];
    for (let i = itemsStartIndex; i < itemsEndIndex; i++) {
      const line = lines[i].trim();
      if (line.length > 0) {
        // Look for lines that contain numbers and might be items
        const numberMatch = line.match(/^(\d+)\s+(.+)/);
        if (numberMatch) {
          const itemNumber = numberMatch[1];
          const restOfLine = numberMatch[2];
          
          // Try to extract quantity, price, and total from the rest of the line
          const numbers = restOfLine.match(/(\d+\.?\d*)/g);
          if (numbers && numbers.length >= 2) {
            const item = {
              number: itemNumber,
              name: 'Item ' + itemNumber, // Generic name
              unit: 'бр',
              quantity: parseFloat(numbers[0]) || 1,
              price: parseFloat(numbers[1]) || 0,
              total: parseFloat(numbers[2]) || parseFloat(numbers[1]) || 0
            };
            
            if (!isNaN(item.quantity) && !isNaN(item.price) && !isNaN(item.total)) {
              console.log('🔧 Extracted raw item:', item);
              rawItems.push(item);
            }
          }
        }
      }
    }
    
    if (rawItems.length > 0) {
      console.log('🔍 Extracted', rawItems.length, 'items from raw OCR');
      return rawItems;
    }
    
    console.log('⚠️ No items could be extracted from OCR data');
    return [];
  };

  useEffect(() => {
    fetchInvoiceDetails();
  }, [id]);

  // Auto-dismiss success messages after 5 seconds
  useEffect(() => {
    if (message.type === 'success' && message.text) {
      const timer = setTimeout(() => {
        setMessage({ type: '', text: '' });
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [message]);

  // Calculate totals using useMemo to avoid recalculation on every render
  const { extractedItems, subtotal, taxAmount, totalAmount, vatRate } = useMemo(() => {
    // Use line items from database if available, otherwise fall back to OCR extraction
    let items = [];
    
    console.log('🔍 Invoice data for line items:', {
      hasInvoice: !!invoice,
      hasLineItems: !!(invoice?.lineItems),
      lineItemsLength: invoice?.lineItems?.length || 0,
      hasOcrData: !!(invoice?.ocrData?.extractedText)
    });
    
    if (invoice?.lineItems && invoice.lineItems.length > 0) {
      // Use line items from database
      items = invoice.lineItems;
      console.log('📦 Using line items from database:', items);
    } else if (invoice?.ocrData?.extractedText) {
      // Fall back to OCR extraction
      items = extractItemsFromOCR(invoice.ocrData.extractedText);
      console.log('📦 Using line items from OCR extraction:', items);
    } else {
      console.log('⚠️ No line items found in database or OCR');
    }
    
    const subtotal = items.reduce((sum, item) => sum + (item.total || 0), 0);
    const vatRate = 20;
    const taxAmount = subtotal * (vatRate / 100);
    const totalAmount = subtotal + taxAmount;
    
    console.log('💰 Calculated totals:', { subtotal, taxAmount, totalAmount, itemCount: items.length });
    
    return { extractedItems: items, subtotal, taxAmount, totalAmount, vatRate };
  }, [invoice?.lineItems, invoice?.ocrData?.extractedText]);

  // Update the invoice total amount in MongoDB if it's different from the calculated total
  useEffect(() => {
    if (invoice && totalAmount > 0 && Math.abs(invoice.totalAmount - totalAmount) > 0.01) {
      updateInvoiceTotal(totalAmount);
    }
  }, [invoice, totalAmount]);

  const fetchInvoiceDetails = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('jwtToken');
      // Add cache-busting parameter to ensure fresh data
      const response = await fetch(`/api/incoming-invoices/${id}?t=${Date.now()}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setInvoice(data.invoice);
        
        // If there's a partner, fetch partner details
        if (data.invoice.supplierName) {
          await fetchPartnerDetails(data.invoice.supplierName);
        }
      } else {
        throw new Error(t('incomingInvoiceDetails.failedToFetchInvoiceDetails'));
      }
    } catch (error) {
      console.error('Error fetching invoice details:', error);
      setMessage({ type: 'error', text: t('incomingInvoiceDetails.failedToLoadInvoiceDetails') });
    } finally {
      setLoading(false);
    }
  };

  const fetchPartnerDetails = async (supplierName) => {
    try {
      const token = localStorage.getItem('jwtToken');
      // Add cache-busting parameter to ensure fresh data
      const response = await fetch(`/api/finance/partners?search=${encodeURIComponent(supplierName)}&t=${Date.now()}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.partners && data.partners.length > 0) {
          setPartner(data.partners[0]);
        }
      }
    } catch (error) {
      console.error('Error fetching partner details:', error);
    }
  };

  const updateInvoiceTotal = async (newTotalAmount) => {
    try {
      const token = localStorage.getItem('jwtToken');
      const response = await fetch(`/api/incoming-invoices/${id}/total`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ totalAmount: newTotalAmount })
      });

      if (response.ok) {
        const data = await response.json();
        setInvoice(data.invoice);
        console.log('💰 Updated invoice total amount in database:', newTotalAmount);
      }
    } catch (error) {
      console.error('Error updating invoice total:', error);
    }
  };

  const handleDelete = async () => {
    try {
      const token = localStorage.getItem('jwtToken');
      const response = await fetch(`/api/incoming-invoices/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        setMessage({ type: 'success', text: t('incomingInvoiceDetails.invoiceDeletedSuccessfully') });
        setTimeout(() => {
          navigate('/admin/dashboard/incoming-invoices');
        }, 1500);
      } else {
        throw new Error(t('incomingInvoiceDetails.failedToDeleteInvoice'));
      }
    } catch (error) {
      console.error('Error deleting invoice:', error);
      setMessage({ type: 'error', text: t('incomingInvoiceDetails.failedToDeleteInvoice') });
    }
  };

  const updateStatus = async (newStatus) => {
    try {
      setSaving(true);
      const token = localStorage.getItem('jwtToken');
      const response = await fetch(`/api/incoming-invoices/${id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: newStatus })
      });

      if (response.ok) {
        setMessage({ type: 'success', text: t('incomingInvoiceDetails.invoiceStatusUpdatedSuccessfully') });
        fetchInvoiceDetails();
      } else {
        throw new Error(t('incomingInvoiceDetails.failedToUpdateInvoiceStatus'));
      }
    } catch (error) {
      console.error('Error updating invoice status:', error);
      setMessage({ type: 'error', text: t('incomingInvoiceDetails.failedToUpdateInvoiceStatus') });
    } finally {
      setSaving(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Paid':
        return 'bg-green-100 text-green-800';
      case 'Pending':
        return 'bg-purple-100 text-purple-800';
      case 'Approved':
        return 'bg-green-100 text-green-800';
      case 'Rejected':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Paid':
        return <CheckCircleIcon className="h-5 w-5" />;
      case 'Pending':
        return <ClockIcon className="h-5 w-5" />;
      case 'Approved':
        return <CheckCircleIcon className="h-5 w-5" />;
      case 'Rejected':
        return <ExclamationTriangleIcon className="h-5 w-5" />;
      default:
        return <DocumentTextIcon className="h-5 w-5" />;
    }
  };

  const formatCurrency = (amount, currency = 'BGN') => {
    return new Intl.NumberFormat('bg-BG', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const convertToEuro = (bgnAmount) => {
    return bgnAmount / EUR_RATE;
  };

  const formatEuro = (amount) => {
    return new Intl.NumberFormat('bg-BG', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('bg-BG');
  };

  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleString('bg-BG');
  };

  const isOverdue = (dueDate) => {
    return new Date(dueDate) < new Date();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex justify-center items-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-500 border-t-transparent mx-auto mb-4"></div>
          <p className="text-gray-600">{t('incomingInvoiceDetails.loadingInvoiceDetails')}</p>
        </div>
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="min-h-screen bg-gray-50 flex justify-center items-center">
        <div className="text-center">
          <DocumentTextIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">{t('incomingInvoiceDetails.invoiceNotFound')}</p>
          <button
            onClick={() => navigate(-1)}
            className="mt-4 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors duration-200"
          >
            {t('incomingInvoiceDetails.backToIncomingInvoices')}
          </button>
        </div>
      </div>
    );
  }



  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <button
                onClick={() => navigate(-1)}
                className="text-gray-600 hover:text-gray-900"
              >
                <ArrowLeftIcon className="h-6 w-6" />
              </button>
              <DocumentTextIcon className="h-8 w-8 text-blue-600" />
              <div>
                <h1 className="text-3xl font-bold text-gray-900">{invoice.invoiceNumber}</h1>
                <p className="text-gray-600">{t('incomingInvoiceDetails.incomingInvoiceDetails')}</p>
              </div>
            </div>
            <div className="flex space-x-3">
              <button className="text-blue-600 hover:text-blue-900 p-2">
                <PencilIcon className="h-5 w-5" />
              </button>
              <button 
                onClick={() => setShowDeleteModal(true)}
                className="text-red-600 hover:text-red-900 p-2"
              >
                <TrashIcon className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Message */}
        {message.text && (
          <div className={`mb-6 p-4 rounded-lg flex items-center space-x-3 ${
            message.type === 'success' 
              ? 'bg-green-50 border border-green-200 text-green-800' 
              : 'bg-red-50 border border-red-200 text-red-800'
          }`}>
            <span>{message.text}</span>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
          {/* Main Content */}
          <div className="lg:col-span-3 space-y-6">
            {/* Invoice Information */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-6">{t('incomingInvoiceDetails.invoiceInformation')}</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Row 1: Invoice Number and Invoice Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">{t('incomingInvoiceDetails.invoiceNumber')}</label>
                  <p className="text-lg font-semibold text-gray-900">{invoice.invoiceNumber}</p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">{t('incomingInvoiceDetails.invoiceType')}</label>
                  <p className="text-gray-900">{t('incomingInvoiceDetails.original')}</p>
                </div>
                
                {/* Row 2: Status and Created At */}
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">{t('incomingInvoiceDetails.status')}</label>
                  <div className="flex items-center space-x-2">
                    {getStatusIcon(invoice.status)}
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(invoice.status)}`}>
                      {t(`incomingInvoiceDetails.status${invoice.status}`)}
                    </span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">{t('incomingInvoiceDetails.createdAt')}</label>
                  <p className="text-gray-900">{formatDateTime(invoice.createdAt)}</p>
                </div>

                {/* Row 3: Issue Date and Due Date */}
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">{t('incomingInvoiceDetails.issueDate')}</label>
                  <p className="text-gray-900">{formatDate(invoice.issueDate)}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">{t('incomingInvoiceDetails.dueDate')}</label>
                  <p className={`${isOverdue(invoice.dueDate) ? 'text-red-600 font-medium' : 'text-gray-900'}`}>
                    {formatDate(invoice.dueDate)}
                  </p>
                </div>

                {/* Row 4: Currency and Payment Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">{t('incomingInvoiceDetails.currency')}</label>
                  <p className="text-gray-900">{invoice.currency}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">{t('incomingInvoiceDetails.paymentType')}</label>
                  <p className="text-gray-900">{invoice.paymentType || t('incomingInvoiceDetails.unknown')}</p>
                </div>
              </div>
            </div>

            {/* Line Items */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <h2 className="text-xl font-semibold text-gray-900 mb-6">{t('incomingInvoiceDetails.lineItems')}</h2>
              

              
              {extractedItems.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {t('incomingInvoiceDetails.description')}
                        </th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {t('incomingInvoiceDetails.quantity')}
                        </th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {t('incomingInvoiceDetails.unitPrice')}
                        </th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {t('incomingInvoiceDetails.total')}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {extractedItems.map((item, index) => (
                        <tr key={index}>
                          <td className="px-4 py-4 text-sm text-gray-900">
                            <div className="font-medium">{item.name}</div>
                          </td>
                          <td className="px-4 py-4 text-sm text-gray-900 text-right">
                            {item.quantity}
                          </td>
                          <td className="px-4 py-4 text-sm text-gray-900 text-right">
                            {formatCurrency(item.price, invoice.currency)}
                          </td>
                          <td className="px-4 py-4 text-sm font-medium text-gray-900 text-right">
                            <div>{formatCurrency(item.total, invoice.currency)}</div>
                            <div className="text-xs text-blue-600">{formatEuro(convertToEuro(item.total))}</div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  {t('incomingInvoiceDetails.noItemsExtractedFromOCR')}
                </div>
              )}

              {/* Totals */}
              <div className="mt-6 border-t border-gray-200 pt-6">
                <div className="space-y-2">
                  <div className="flex justify-between text-lg">
                    <span className="text-gray-600">{t('incomingInvoiceDetails.subtotal')}:</span>
                    <span className="font-medium">{formatCurrency(subtotal, invoice.currency)}</span>
                  </div>
                  <div className="flex justify-between text-lg">
                    <span className="text-gray-600">{t('incomingInvoiceDetails.vat')} ({vatRate}%):</span>
                    <span className="font-medium">{formatCurrency(taxAmount, invoice.currency)}</span>
                  </div>
                  <div className="border-t border-gray-200 pt-2">
                    <div className="flex justify-between text-xl font-bold">
                      <span className="text-gray-900">{t('incomingInvoiceDetails.totalAmount')}:</span>
                      <span className="text-blue-600">{formatCurrency(totalAmount, invoice.currency)}</span>
                    </div>
                  </div>
                </div>
                
                {/* Euro Conversion */}
                <div className="mt-4 border-t border-gray-200 pt-4 bg-gray-50 rounded-lg p-4">
                  <h4 className="text-sm font-semibold text-gray-900 mb-3">{t('incomingInvoiceDetails.euroEquivalent')} (1 EUR = 1.95583 BGN)</h4>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">{t('incomingInvoiceDetails.subtotal')}:</span>
                      <span className="font-medium">{formatEuro(convertToEuro(subtotal))}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">{t('incomingInvoiceDetails.vat')} ({vatRate}%):</span>
                      <span className="font-medium">{formatEuro(convertToEuro(taxAmount))}</span>
                    </div>
                    <div className="border-t border-gray-300 pt-2">
                      <div className="flex justify-between text-base font-bold">
                        <span className="text-gray-900">{t('incomingInvoiceDetails.totalAmount')}:</span>
                        <span className="text-green-600">{formatEuro(convertToEuro(totalAmount))}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>


          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Partner Info */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('incomingInvoiceDetails.partnerDetails')}</h3>
              
              {partner ? (
                <div className="text-sm text-gray-900">
                  <p className="font-medium">{partner.name}</p>
                  <p>{partner.address}</p>
                  <p>{partner.city}, {partner.country}</p>
                  <p>{t('incomingInvoiceDetails.contact')}: {partner.contactPerson}</p>
                  <p>{t('incomingInvoiceDetails.email')}: {partner.email}</p>
                  <p>{t('incomingInvoiceDetails.phone')}: {partner.phone}</p>
                  <p>{t('incomingInvoiceDetails.taxId')}: {partner.taxId}</p>
                  <p>{t('incomingInvoiceDetails.vatTaxId')}: {partner.vatTaxId}</p>
                  {partner.bankDetails && (
                    <div className="mt-2">
                      <p>{t('incomingInvoiceDetails.bank')}: {partner.bankDetails.bankName}</p>
                      <p>SWIFT: {partner.bankDetails.swiftCode}</p>
                      <p>IBAN: {partner.bankDetails.iban}</p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-sm text-gray-500">
                  <p>{t('incomingInvoiceDetails.noPartnerDetailsAvailable')}</p>
                </div>
              )}
            </div>

            {/* Payment Status */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('incomingInvoiceDetails.paymentStatus')}</h3>
              
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">{t('incomingInvoiceDetails.totalAmount')}:</span>
                  <span className="text-sm font-medium">{formatCurrency(invoice.totalAmount, invoice.currency)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">{t('incomingInvoiceDetails.amountPaid')}:</span>
                  <span className="text-sm font-medium text-green-600">{formatCurrency(0, invoice.currency)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">{t('incomingInvoiceDetails.remaining')}:</span>
                  <span className="text-sm font-medium text-red-600">
                    {formatCurrency(invoice.totalAmount, invoice.currency)}
                  </span>
                </div>
              </div>
              
              {/* Euro Conversion for Payment Status */}
              <div className="mt-3 pt-3 border-t border-gray-200 bg-blue-50 rounded-lg p-3">
                <h4 className="text-xs font-semibold text-gray-900 mb-2">{t('incomingInvoiceDetails.euroEquivalent')}</h4>
                <div className="space-y-1">
                  <div className="flex justify-between text-xs">
                    <span className="text-gray-600">{t('incomingInvoiceDetails.total')}:</span>
                    <span className="font-medium">{formatEuro(convertToEuro(invoice.totalAmount))}</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-gray-600">{t('incomingInvoiceDetails.paid')}:</span>
                    <span className="font-medium text-green-600">{formatEuro(0)}</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-gray-600">{t('incomingInvoiceDetails.remaining')}:</span>
                    <span className="font-medium text-red-600">
                      {formatEuro(convertToEuro(invoice.totalAmount))}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Status Actions */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('incomingInvoiceDetails.statusActions')}</h3>
              
              <div className="space-y-2">
                {invoice.status === 'Pending' && (
                  <button
                    onClick={() => updateStatus('Approved')}
                    disabled={saving}
                    className="w-full bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors duration-200 disabled:opacity-50"
                  >
                    {t('incomingInvoiceDetails.markAsApproved')}
                  </button>
                )}
                
                {invoice.status === 'Pending' && (
                  <button
                    onClick={() => updateStatus('Rejected')}
                    disabled={saving}
                    className="w-full bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors duration-200 disabled:opacity-50"
                  >
                    {t('incomingInvoiceDetails.markAsRejected')}
                  </button>
                )}
                
                {invoice.status === 'Approved' && (
                  <button
                    onClick={() => updateStatus('Paid')}
                    disabled={saving}
                    className="w-full bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors duration-200 disabled:opacity-50"
                  >
                    {t('incomingInvoiceDetails.markAsPaid')}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3 text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mt-4">{t('incomingInvoiceDetails.deleteInvoice')}</h3>
              <div className="mt-2 px-7 py-3">
                <p className="text-sm text-gray-500">
                  {t('incomingInvoiceDetails.confirmDeleteInvoice')} <strong>{invoice.invoiceNumber}</strong>? 
                  {t('incomingInvoiceDetails.actionCannotBeUndone')}
                </p>
              </div>
              <div className="flex justify-center space-x-3 mt-4">
                <button
                  onClick={() => setShowDeleteModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors duration-200"
                >
                  {t('incomingInvoiceDetails.cancel')}
                </button>
                <button
                  onClick={handleDelete}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors duration-200"
                >
                  {t('incomingInvoiceDetails.delete')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default IncomingInvoiceDetails; 